import {useState, useEffect} from 'react';
import remoteConfig from '@react-native-firebase/remote-config';

interface InfluencerTableData {
  followers: string;
  liters: string;
}

interface SupportHelpItem {
  type: string;
  value: string;
}

export interface EncherTanqueStep {
  title: string;
  text: string;
}

export interface RichTextItem {
  type: 'text' | 'textBold';
  value: string;
}

const defaultInfluencerTableData: InfluencerTableData[] = [];

const defaultEncherTanqueSteps: EncherTanqueStep[] = [
  {
    title: 'Medimos o que sobrou',
    text: 'Após o abastecimento, calculamos a diferença entre o que foi pago e o que realmente coube no tanque.',
  },
  {
    title: 'Litros inteiros viram cupom',
    text: 'Cada litro não utilizado é convertido em um cupom de abastecimento para o seu próximo abastecimento.',
  },
  {
    title: 'Mililitros viram BaratinhaZ',
    text: 'A fração restante em mililitros é convertida em BaratinhaZ.',
  },
];

const defaultEncherTanqueExample: RichTextItem[] = [
  {type: 'text', value: 'Você pagou por '},
  {type: 'textBold', value: '50 litros'},
  {type: 'text', value: ', mas o tanque comportou '},
  {type: 'textBold', value: '44,5 L'},
  {type: 'text', value: '.\nVocê recebe um '},
  {type: 'textBold', value: 'cupom de 5 L'},
  {type: 'text', value: ' + os '},
  {type: 'textBold', value: '500 ml'},
  {type: 'text', value: ' viram '},
  {type: 'textBold', value: 'BaratinhaZ'},
  {type: 'text', value: ' na carteira.'},
];

export function useRemoteConfig(isOpen: boolean) {
  const [influencerTableData, setInfluencerTableData] = useState<
    InfluencerTableData[]
  >(defaultInfluencerTableData);

  const [prazoPixReembolso, setPrazoPixReembolso] = useState(0);
  const [reembolsoTextoInicial, setReembolsoTextoInicial] = useState('');
  const [reembolsoTextoFinal, setReembolsoTextoFinal] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [supportHelpText, setSupportHelpText] = useState<SupportHelpItem[]>([]);

  const [encherTanqueInfoTitle, setEncherTanqueInfoTitle] = useState(
    'Nenhum litro desperdiçado',
  );
  const [encherTanqueInfoDesc, setEncherTanqueInfoDesc] = useState(
    'Se o tanque encher antes da quantidade escolhida, devolvemos o valor restante automaticamente sem precisar pedir.',
  );
  const [encherTanqueInfoSteps, setEncherTanqueInfoSteps] = useState<
    EncherTanqueStep[]
  >(defaultEncherTanqueSteps);
  const [encherTanqueInfoExample, setEncherTanqueInfoExample] = useState<
    RichTextItem[]
  >(defaultEncherTanqueExample);

  const [numeroSuporteInfluenciador, setNumeroSuporteInfluenciador] = useState('');


  useEffect(() => {
    if (!isOpen) {return;}

    const initializeRemoteConfig = async () => {
      try {
        await remoteConfig().setDefaults({
          influencer_table_data: JSON.stringify(defaultInfluencerTableData),
          reembolso_prazo_pix: 0,
          reembolso_texto_inicial: '',
          reembolso_texto_final: '',
          encherTanque_info_title: 'Nenhum litro desperdiçado',
          encherTanque_info_desc:
            'Se o tanque encher antes da quantidade escolhida, devolvemos o valor restante automaticamente sem precisar pedir.',
          encherTanque_info_steps: JSON.stringify(defaultEncherTanqueSteps),
          encherTanque_info_example: JSON.stringify(defaultEncherTanqueExample),
          Numero_suporte_influenciador: ''
        });

        await remoteConfig().setConfigSettings({
          minimumFetchIntervalMillis: 60 * 60 * 1000,
        });

        await remoteConfig().fetchAndActivate();

        const tableDataString = remoteConfig()
          .getValue('influencer_table_data')
          .asString();
        console.log('[RemoteConfig] influencer_table_data:', tableDataString);

        const reembolsoPixValue = remoteConfig()
          .getValue('reembolso_prazo_pix')
          .asString();

        const reembolsoTextoInicialValue = remoteConfig()
          .getValue('reembolso_texto_inicial')
          .asString();

        const reembolsoTextoFinalValue = remoteConfig()
          .getValue('reembolso_texto_final')
          .asString();

        const supportHelpTextValue = remoteConfig()
          .getValue('suporte_texto_ajuda')
          .asString();
        console.log('[RemoteConfig] suporte_texto_ajuda:', supportHelpTextValue);

        const encherTanqueInfoTitleValue = remoteConfig()
          .getValue('encherTanque_info_title')
          .asString();

        const encherTanqueInfoDescValue = remoteConfig()
          .getValue('encherTanque_info_desc')
          .asString();

        const encherTanqueInfoStepsValue = remoteConfig()
          .getValue('encherTanque_info_steps')
          .asString();

        const encherTanqueInfoExampleValue = remoteConfig()
          .getValue('encherTanque_info_example')
          .asString();
          const numeroSuporteInfluenciadorValue = remoteConfig().getValue('Numero_suporte_influenciador').asString();

        if (tableDataString) {
          try {
            const parsedData = JSON.parse(tableDataString);
            if (Array.isArray(parsedData)) {
              setInfluencerTableData(parsedData);
            }
          } catch (error) {
            console.warn('Erro ao fazer parse dos dados do Remote Config:', error);
          }
        }

        if (reembolsoPixValue) {
          setPrazoPixReembolso(Number(reembolsoPixValue));
        }

        if (reembolsoTextoInicialValue) {
          setReembolsoTextoInicial(reembolsoTextoInicialValue);
        }

        if (reembolsoTextoFinalValue) {
          setReembolsoTextoFinal(reembolsoTextoFinalValue);
        }

        if (supportHelpTextValue) {
          try {
            const parsedData = JSON.parse(supportHelpTextValue);
            if (Array.isArray(parsedData)) {
              setSupportHelpText(parsedData);
            }
          } catch (error) {
            console.warn('Erro ao fazer parse do suporte_texto_ajuda:', error);
          }
        }

        if (encherTanqueInfoTitleValue) {
          setEncherTanqueInfoTitle(encherTanqueInfoTitleValue);
        }

        if (encherTanqueInfoDescValue) {
          setEncherTanqueInfoDesc(encherTanqueInfoDescValue);
        }

        if (encherTanqueInfoStepsValue) {
          try {
            const parsedSteps = JSON.parse(encherTanqueInfoStepsValue);
            if (Array.isArray(parsedSteps) && parsedSteps.length === 3) {
              setEncherTanqueInfoSteps(parsedSteps);
            }
          } catch (error) {
            console.warn('Erro ao fazer parse do encherTanque_info_steps:', error);
          }
        }

        if (encherTanqueInfoExampleValue) {
          try {
            const parsedExample = JSON.parse(encherTanqueInfoExampleValue);
            if (Array.isArray(parsedExample)) {
              setEncherTanqueInfoExample(parsedExample);
            }
          } catch (error) {
            console.warn('Erro ao fazer parse do encherTanque_info_example:', error);
          }
        }
        
        
        if (numeroSuporteInfluenciadorValue) {
          setNumeroSuporteInfluenciador(numeroSuporteInfluenciadorValue);
        }



      } catch (error) {
        console.warn('Erro ao carregar Remote Config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeRemoteConfig();
  }, [isOpen]);

  return {
    influencerTableData,
    prazoPixReembolso,
    reembolsoTextoInicial,
    reembolsoTextoFinal,
    supportHelpText,
    isLoading,
    encherTanqueInfoTitle,
    encherTanqueInfoDesc,
    encherTanqueInfoSteps,
    encherTanqueInfoExample,
    numeroSuporteInfluenciador
  };
}
